import React, { useMemo, useRef, useState } from "react";
import { BASE_URL } from "./apiConfig";
import StatusPanel from "./components/StatusPanel";
import IconButton from "@mui/material/IconButton";
import ShareIcon from "@mui/icons-material/Share";
import Tooltip from "@mui/material/Tooltip";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Portal from "@mui/material/Portal";
import { auth } from "./firebase"; // <-- Import your firebase instance

type UploadStatus = "pending" | "uploading" | "done" | "error";
type UploadedFile = {
  file: File;
  id: string;
  size: number;
  progress: number; // 0..100
  status: UploadStatus;
};
// Allow-list
const ALLOWED_EXTS = new Set(["csv", "json", "xls", "xlsx", "xml"]);
const isAllowedFile = (f: File) => {
  const ext = f.name.split(".").pop()?.toLowerCase() || "";
  return ALLOWED_EXTS.has(ext);
};

export default function DocumentUpload() {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [analysisDone, setAnalysisDone] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showLoginWarning, setShowLoginWarning] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null); // <-- Add this

  const inputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  // timers for simulated pipeline
  const timersRef = useRef<number[]>([]);

  const totalBytes = useMemo(() => files.reduce((s, f) => s + f.size, 0), [files]);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const v = parseFloat((bytes / Math.pow(k, i)).toFixed(1));
    return `${v}${sizes[i]}`;
  };


  // NEW: Check login status and userId
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isLoggedIn = !!localStorage.getItem("authToken") && (userRole === "admin" || userRole === "staff");

  // Helper to force Snackbar to reopen even on repeated attempts
  const reopenLoginWarning = React.useCallback(() => {
    setShowLoginWarning(false);
    setTimeout(() => setShowLoginWarning(true), 0);
  }, []);

  // clear all simulated timers
  const clearTimers = () => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
  };

  // start simulated pipeline status
  const startSimulatedPipeline = () => {
    clearTimers();
    setStatusMessages(["Data cleaning started"]);

    // 30s cleaning
    timersRef.current.push(
      window.setTimeout(() => {
        setStatusMessages((p) => [...p, "Data cleaning completed", "Generating insights"]);
      }, 30_000)
    );

    // +40s insights
    timersRef.current.push(
      window.setTimeout(() => {
        setStatusMessages((p) => [...p, "Insights generation completed", "Visualization started"]);
      }, 70_000)
    );

    // +30s visualization
    timersRef.current.push(
      window.setTimeout(() => {
        setStatusMessages((p) => [...p, "Visualization completed", "Summarizing insights started"]);
      }, 100_000)
    );

    // +20s summarizing -> finished
    timersRef.current.push(
      window.setTimeout(() => {
        setStatusMessages((p) => [
          ...p,
          "Summarizing insights completed",
          "Pipeline finished",
        ]);
        setAnalysisDone(true);
        setIsAnalyzing(false);
      }, 120_000)
    );
  };

  // complete everything immediately (when API responds early)
  const finishPipelineEarly = () => {
    clearTimers();
    setStatusMessages((p) => [
      ...p,
      "Data cleaning started",
      "Data cleaning completed",
      "Generating insights",
      "Insights generation completed",
      "Visualization started",
      "Visualization completed",
      "Summarizing insights started",
      "Summarizing insights completed",
      "Pipeline finished",
    ]);
    setAnalysisDone(true);
    setIsAnalyzing(false);
  };

  const handleFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles || isUploading) return;
    if (!isLoggedIn) {
      reopenLoginWarning();
      return;
    }

    const incoming = Array.from(selectedFiles);
    const allowed = incoming.filter(isAllowedFile);
    const rejected = incoming.filter((f) => !isAllowedFile(f));
    if (rejected.length) {
      window.alert("Only CSV, JSON, XLS, XLSX, XML files are allowed.");
    }

    const newFiles: UploadedFile[] = allowed.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${file.lastModified}`,
      size: file.size,
      progress: 0,
      status: "pending",
    }));
    setFiles((prev) => [
      ...prev,
      ...newFiles.filter((nf) => !prev.some((pf) => pf.id === nf.id)),
    ]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (id: string) => {
    if (isUploading) return;
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleCancel = () => {
    if (isUploading) {
      try { xhrRef.current?.abort(); } catch {}
      clearTimers();
      setIsUploading(false);
      setIsAnalyzing(false);
      setStatusMessages((p) => [...p, "❌ Upload canceled"]);
    } else {
      clearTimers();
      setFiles([]);
      setStatusMessages([]);
      setAnalysisDone(false);
      setUploadProgress(0);
    }
  };

  
  const handleAnalyze = async () => {
    if (files.length === 0 || isUploading) return;
    if (!isLoggedIn) {
      reopenLoginWarning();
      return;
    }
    setIsAnalyzing(true);
    setStatusMessages([]);
    setAnalysisDone(false);
    setUploadProgress(0);
    setBackendError(null); // <-- Reset error

    // Start simulated pipeline immediately
    startSimulatedPipeline();

    try {
      setIsUploading(true);
      setFiles((prev) => prev.map((f) => ({ ...f, status: "uploading", progress: 0 })));

      const formData = new FormData();
      formData.append("userId", userId || "");
      files.forEach(({ file }) => formData.append("files", file));

      // Firebase ID token
      const currentUser = auth.currentUser;
      if (!currentUser) {
        reopenLoginWarning();
        throw new Error("No authenticated user");
      }
      const idToken = await currentUser.getIdToken(true);

      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;
      xhr.open("POST", `${BASE_URL}/upload`);
      xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);

      // Compute offsets for per-file progress
      const sizes = files.map((f) => f.size);
      const offsets: number[] = [];
      sizes.reduce((acc, s, i) => {
        offsets[i] = acc;
        return acc + s;
      }, 0);

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const loaded = e.loaded;
        const total = e.total || totalBytes || 1;

        // Update aggregate
        setUploadProgress(Math.round((loaded / total) * 100));

        // Update per-file progress using offsets
        setFiles((prev) =>
          prev.map((f, idx) => {
            const start = offsets[idx];
            const end = start + sizes[idx];
            let p = 0;
            if (loaded >= end) p = 100;
            else if (loaded <= start) p = 0;
            else p = Math.floor(((loaded - start) / sizes[idx]) * 100);
            return {
              ...f,
              status: p >= 100 ? "done" : "uploading",
              progress: Math.min(100, Math.max(0, p)),
            };
          })
        );
      };

      const result = await new Promise<Response>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(new Response(xhr.responseText, { status: xhr.status }));
          } else {
            // Try to parse error message from backend
            let errorMsg = "HTTP " + xhr.status;
            try {
              const resp = JSON.parse(xhr.responseText);
              if (resp?.error) errorMsg = resp.error;
              else if (resp?.message) errorMsg = resp.message;
            } catch {}
            reject(new Error(errorMsg));
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.onabort = () => reject(new Error("Upload aborted"));
        xhr.send(formData);
      });

      setUploadProgress(100);
      setFiles((prev) => prev.map((f) => ({ ...f, progress: 100, status: "done" })));

      try { await result.json(); } catch {}

      // API responded before simulated pipeline completes -> finish instantly
      finishPipelineEarly();
    } catch (error: any) {
      console.error(error);
      clearTimers();
      setFiles((prev) => prev.map((f) => ({ ...f, status: "error" })));
      setStatusMessages([]); // <-- Clear status messages
      setIsAnalyzing(false); // <-- Stop analyzing
      setAnalysisDone(false); // <-- Not done
      setBackendError(error?.message || "Upload failed"); // <-- Set error message
    } finally {
      setIsUploading(false);
    }
  };

  React.useEffect(() => () => {
    xhrRef.current?.abort();
    clearTimers();
  }, []);

  const shareUrl = "https://trentiq.bigdatamatica.com";
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Tata Trent Insights",
          text: "Check out the results on Tata Trent Insights!",
          url: shareUrl,
        });
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  // NEW: decide which pane to show (status replaces upload)
  const showStatus = isAnalyzing || statusMessages.length > 0 || analysisDone;

  return (
    <div className="p-0">
      <div
        className="rounded-xl overflow-hidden border border-slate-800"
        style={{
          background: "rgba(16,18,19,1)",
          boxShadow: "0px 4px 50px 0px rgba(0,0,0,0.36)",
          color: "rgba(255,255,255,1)",
        }}
      >
        <div className="px-5 pt-6 pb-0 flex flex-col gap-1">
          <h2
            className="font-inter font-semibold text-[20px] leading-[100%] tracking-[0.01em] align-middle"
            style={{
              color: "rgba(255,255,255,1)",
              letterSpacing: "1%",
              fontWeight: 400,
              fontFamily: "Inter, sans-serif",
            }}
          >
            File Upload
          </h2>
          <div
            className="font-inter font-normal text-[16px] leading-[24px] tracking-[0.04em] align-middle"
            style={{
              color: "rgba(215,215,215,1)",
              letterSpacing: "4%",
              fontWeight: 400,
              fontFamily: "Inter, sans-serif",
              marginTop: "2px",
              marginBottom: "20px",
            }}
          >
            Choose a file and upload securely to proceed.
          </div>
        </div>

        {/* STATUS VIEW (replaces upload area) */}
        {showStatus ? (
          <div className="px-5 pb-5">
            <StatusPanel messages={statusMessages} isAnalyzing={isAnalyzing} variant="dark" />
            {analysisDone && (
              <div
                className="mt-4 border border-slate-800 rounded-lg p-4 flex flex-col items-center gap-2 relative"
                style={{ background: "#000" }}
              >
                <div className="absolute top-2 right-2">
                  <Tooltip title="Share">
                    <IconButton
                      onClick={handleShare}
                      aria-label="share"
                      size="small"
                      style={{ color: "#fff" }}
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: 500,
                    textDecoration: "underline",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {shareUrl}
                </a>
                {/* Back to Upload button */}
                <button
                  type="button"
                  className="mt-4 px-4 py-2 rounded-lg border border-slate-700 text-base font-medium transition-all"
                  style={{
                    color: "rgba(207, 207, 207, 1)",
                    background: "#111",
                  }}
                  onClick={() => {
                    setFiles([]);
                    setStatusMessages([]);
                    setAnalysisDone(false);
                    setUploadProgress(0);
                    setIsAnalyzing(false);
                    setBackendError(null);
                  }}
                >
                  Back to Upload
                </button>
              </div>
            )}
          </div>
        ) : (
          /* UPLOAD VIEW */
          <>
            <div
              className="mx-5 mb-4 rounded-lg border-2 border-dashed"
              style={{
                background: "rgba(16,18,19,1)",
                borderColor: "#3B4252",
              }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="p-6 flex flex-col items-center text-center gap-3" style={{ width: "100%" }}>
                <div className="w-14 h-14 rounded-lg bg-[#rgba(16,18,19,1)] border border-[#3B4252] flex items-center justify-center">
                  <UploadFileIcon style={{ fontSize: 36, color: "rgba(207, 207, 207, 1)" }} />
                </div>
                <div
                  className="font-inter font-medium text-[16px] leading-[24px] align-middle"
                  style={{
                    color: "rgba(207, 207, 207, 1)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Drag and drop your files
                </div>
                <div
                  className="font-inter text-[14px] leading-[24px] align-middle"
                  style={{
                    color: "rgba(125, 125, 125, 1)",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  CSV, XLS, XLSX, JSON, XML (up to 50MB each)
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    style={{
                      width: 120,
                      height: 36,
                      borderRadius: 4,
                      padding: "8px 16px",
                      border: "1px solid rgba(70, 72, 72, 1)",
                      background: "rgba(16, 18, 19, 1)",
                      color: "#fff",
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: 14,
                      opacity: 1,
                      cursor: "pointer",
                      outline: "none",
                    }}
                    className="hover:bg-slate-800 disabled:opacity-60"
                    onClick={() => {
                      if (inputRef.current) inputRef.current.value = "";
                      inputRef.current?.click();
                    }}
                    disabled={isUploading}
                  >
                    Select File
                  </button>
                  <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept=".csv,text/csv,.json,application/json,.xls,application/vnd.ms-excel,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xml,application/xml,text/xml"
                    className="hidden"
                    onChange={(e) => {
                      handleFiles(e.target.files);
                      // Allow selecting the same file again
                      e.currentTarget.value = "";
                    }}
                  />
                </div>
              </div>
            </div>

            {files.length > 0 && (
              <div className="px-5 pb-4">
                <div className="text-sm mb-2" style={{ color: "rgba(207, 207, 207, 1)" }}>Uploaded Files</div>
                <div className="space-y-3">
                  {files.map(({ file, id, size, progress, status }) => (
                    <div
                      key={id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3"
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-md border border-slate-700 flex items-center justify-center shrink-0" style={{ background: "#000" }}>
                          <ArticleIcon style={{ color: "rgba(207, 207, 207, 1)", fontSize: 24 }} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-slate-100 truncate">{file.name}</div>
                          <div className="text-xs text-slate-500">
                            {formatBytes(size)} {status === "uploading" ? `• ${progress}%` : null}
                          </div>
                          {(status === "uploading" || status === "done" || status === "error") && (
                            <div className="mt-2">
                              <div className="w-full h-1.5 bg-slate-800 rounded">
                                <div
                                  className={`h-1.5 rounded ${
                                    status === "error"
                                      ? "bg-rose-500"
                                      : "bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400"
                                  }`}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <div className="mt-1 text-[11px]">
                                {status === "uploading" && <span className="text-slate-400">Uploading…</span>}
                                {status === "done" && <span className="text-emerald-400">Uploaded Successfully</span>}
                                {status === "error" && <span className="text-rose-400">Upload failed</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        className={`text-white hover:text-rose-400 shrink-0 ${
                          isUploading ? "opacity-40 cursor-not-allowed" : ""
                        }`}
                        onClick={() => handleRemove(id)}
                        disabled={isUploading}
                        aria-label={`Remove ${file.name}`}
                        title="Remove"
                      >
                        <DeleteIcon fontSize="small" style={{ color: "white" }} />
                      </button>
                    </div>
                  ))}
                </div>

                {isUploading && (
                  <div className="mt-3">
                    <div className="w-full h-2 bg-slate-800 rounded">
                      <div
                        className="h-2 rounded bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-slate-400">{uploadProgress}%</div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between gap-3">
                  <button
                    className="w-1/2 min-w-[120px] px-4 py-2 rounded-lg border border-slate-700 text-base font-medium disabled:opacity-60 transition-all"
                    type="button"
                    onClick={handleCancel}
                    style={{
                      color: "rgba(207, 207, 207, 1)",
                      background: "#000",
                    }}
                  >
                    {isUploading ? "Cancel" : "Clear"}
                  </button>
                  <button
                    className="w-1/2 min-w-[120px] px-4 py-2 rounded-lg bg-white text-slate-900 text-base font-medium hover:bg-slate-100 disabled:opacity-60 transition-all"
                    onClick={handleAnalyze}
                    disabled={isUploading || files.length === 0}
                  >
                    {isUploading ? "Uploading…" : isAnalyzing ? "Analyzing…" : "Analyze"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Portal the Snackbar so it renders above the Navbar's stacking context */}
      <Portal>
        <Snackbar
          open={showLoginWarning}
          autoHideDuration={3000}
          onClose={() => setShowLoginWarning(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
            top: { xs: "72px", sm: "30px" },
          }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => setShowLoginWarning(false)}
            sx={{
              width: "100%",
              minWidth: "300px",
              boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            Only admin or staff users can upload files. Please sign in with the correct account.
          </Alert>
        </Snackbar>

        {/* Backend error popup */}
        <Snackbar
          open={!!backendError}
          autoHideDuration={5000}
          onClose={() => setBackendError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 2,
            top: { xs: "110px", sm: "70px" },
          }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setBackendError(null)}
            sx={{
              width: "100%",
              minWidth: "300px",
              boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            {backendError}
          </Alert>
        </Snackbar>
      </Portal>
    </div>
  );
}

